import { Box, HStack, Icon, Label, Text } from '@medely/ui-kit';
import { HelpCenterLink } from '@mp/components/helpCenterLink';
import { JobDetailsListItem } from '@mp/components/jobs';
import { ClickableText } from '@mp/components/clickableText';
import React from 'react';
import { CompensationModal } from './compensationModal/CompensationModal';
import useDialog from '@mp/hooks/useDialog';
import { useGate } from '@mp/hooks';
import { gateNames } from '@mp/constants';
import { AssignmentForAssignmentDetailsGql } from '../../graphql';
import { FragmentType, parseFragmentData } from '@mp/graphql/generated';

const GUARANTEED_MINIMUM_HOURS = 4;

type DetailsBannersProps = {
  assignment?: FragmentType<typeof AssignmentForAssignmentDetailsGql> | null;
  smallIcon?: boolean;
};

export const DetailsBanners = ({ assignment: inputAssignment, smallIcon }: DetailsBannersProps) => {
  const assignment = parseFragmentData(AssignmentForAssignmentDetailsGql, inputAssignment);

  const isW2 = assignment?.is_w2;
  const compensationDetailsDialog = useDialog();
  const isUnpaidBreak = assignment?.track_unpaid_breaks;
  const isNewFacilityGuideFormat = useGate(gateNames.facilityGuide);

  return (
    <Box>
      {!!assignment?.track_unpaid_breaks && !isNewFacilityGuideFormat && (
        <Box py={2}>
          <JobDetailsListItem
            icon={
              <Icon
                name="light-burger-soda"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Unpaid meal break"
            small
          >
            <Text size="m" color="text.secondary">
              {`Shifts have ${assignment?.estimated_unpaid_break_time_minutes} minutes of meal break, which is not included in your total completed hours. `}
              <HelpCenterLink configKey="breaksAndLunches" text="Learn more" />
            </Text>
          </JobDetailsListItem>
        </Box>
      )}
      {assignment?.enforce_guaranteed_minimum_hours && (
        <Box py={2} testId="assignment-additional-details-scheduling-assurance">
          <JobDetailsListItem
            icon={
              <Icon
                name="light-shield-check"
                iconSize={smallIcon ? '14px' : '20px'}
                color="text.primary"
                variant="custom"
              />
            }
            title="Scheduling assurance"
            small
          >
            <Text size="m" color="text.secondary">
              {`Book with confidence knowing ${
                (assignment?.number_of_shifts_per_week ?? 1) * GUARANTEED_MINIMUM_HOURS
              } hours per week are protected by Medely's scheduling assurance guidelines. `}
              <HelpCenterLink configKey="schedulingAssurance" text="Learn more" />
            </Text>
          </JobDetailsListItem>
        </Box>
      )}
      {isNewFacilityGuideFormat && (
        <Box py={2}>
          <HStack alignItems="center" gap={2} testId="meal-break-compensation-info">
            <Icon
              name="light-burger-soda"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
            <Label size="m">{isUnpaidBreak ? 'Unpaid meal break' : 'Paid meal break'}</Label>
          </HStack>
        </Box>
      )}
      <Box py={2}>
        <JobDetailsListItem
          icon={
            <Icon
              name="light-info-circle"
              iconSize={smallIcon ? '14px' : '20px'}
              color="text.primary"
              variant="custom"
            />
          }
          small
        >
          <Text
            size="m"
            color="text.secondary"
            testId="assignment-details-compensation-pay-may-vary-info"
          >
            Hours may vary and can be adjusted by the facility.{' '}
            <ClickableText
              testId="assignment-compensation-info-link"
              onClick={compensationDetailsDialog.openDialog}
            >
              <Text size="m" color="state.teal.secondary">
                Learn more
              </Text>
            </ClickableText>
          </Text>
        </JobDetailsListItem>
      </Box>
      <CompensationModal
        open={compensationDetailsDialog.open}
        onClose={compensationDetailsDialog.closeDialog}
        // @ts-ignore
        isW2={isW2}
      />
    </Box>
  );
};
